import { gql } from '@urql/core';
import React, { useCallback } from 'react';
import {
    NotificationsCardFragment,
    MarkAllAsReadMutation,
    MarkAllAsReadMutationVariables,
    MarkAllAsReadDocument,
    SpinNotificationDataFragment,
} from '../../../generated/spin-graphql';
import { MarkAsReadIcon } from '../../svg/MarkAsReadIcon';
import { NotificationCard } from './NotificationCard';
import { toast } from 'react-toastify';
import { useAuthenticatedMutation } from '../../../client/hooks';

type Props = {
    notifications: NotificationsCardFragment[] | SpinNotificationDataFragment[];
    refetch: () => void;
};

gql`
    fragment NotificationsCard on espin_notification {
        ...NotificationCard
        ...InterestedInCard
        ...ConversationStartedCard
    }
    mutation MarkAllAsRead($_in: [uuid!] = "") {
        update_espin_notification(
            where: { _and: { id: { _in: $_in }, viewed_at: { _is_null: true } } }
            _set: { viewed_at: "now()" }
        ) {
            affected_rows
        }
    }
`;

export const NotificationsCard: React.FC<Props> = ({ notifications, refetch }) => {
    const [_, setAllRead] = useAuthenticatedMutation<MarkAllAsReadMutation, MarkAllAsReadMutationVariables>(
        MarkAllAsReadDocument,
    );

    const readAll = useCallback(async () => {
        if (notifications.length > 0) {
            const result = await setAllRead(
                {
                    _in: notifications.map((n) => n.id),
                },
                { additionalTypenames: ['espin_notification'] },
            );

            if (result.error) {
                toast.error(`There was an error marking all notifications as read`);
                console.error(result.error);
                return;
            }

            toast.success('All notifications marked as read!');
            refetch();
        }
    }, [notifications, setAllRead, refetch]);

    return (
        <div className="notifications-card">
            <div className="title-container">
                <h2 className="title">Recent Notifications</h2>
                <button onClick={() => readAll()} className="button circle-button" title="Mark all as Read">
                    <MarkAsReadIcon />
                </button>
            </div>
            <div className={`content${notifications.length <= 0 ? ' center-content' : ''}`}>
                {notifications.length > 0 ? (
                    <>
                        {notifications.map((notification, index) => (
                            <NotificationCard key={`notification-${index}`} notification={notification} />
                        ))}
                    </>
                ) : (
                    <div className="empty-notifications">
                        <h2 className="notification-title">{"You're all caught up!"}</h2>
                        <p className="notification-subtitle">You have no notifications.</p>
                    </div>
                )}
            </div>
        </div>
    );
};
